import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppointment } from '../../contexts/AppointmentContext';
import { Calendar, Clock, Video, Phone, MessageSquare, Filter, Search, CheckCircle, Stethoscope } from 'lucide-react';
import StartConsultingModal from '../../components/StartConsultingModal';

const DoctorAppointments = () => {
  const navigate = useNavigate();
  const { doctorConfirmAppointment } = useAppointment();
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showConsultingModal, setShowConsultingModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const mockAppointments = [
    {
      id: '1',
      date: '2026-01-30',
      time: '09:00 AM',
      patient: 'Mrs. Ngozi Okeke',
      patientId: 'patient1',
      type: 'Antenatal Check-up',
      mode: 'video',
      status: 'completed',
      scheduledTime: new Date(2026, 0, 30, 9, 0),
    },
    {
      id: '2',
      date: '2026-01-30',
      time: '10:30 AM',
      patient: 'Mrs. Fatima Bello',
      patientId: 'patient2',
      type: 'Postnatal Follow-up',
      mode: 'audio',
      status: 'upcoming',
      scheduledTime: new Date(2026, 0, 30, 10, 30),
    },
    {
      id: '3',
      date: '2026-01-30',
      time: '02:00 PM',
      patient: 'Mrs. Adaobi Nwankwo',
      patientId: 'patient3',
      type: 'Pregnancy Consultation',
      mode: 'video',
      status: 'upcoming',
      scheduledTime: new Date(2026, 0, 30, 14, 0),
    },
    {
      id: '4',
      date: '2026-01-31',
      time: '09:00 AM',
      patient: 'Mrs. Aisha Mohammed',
      patientId: 'patient4',
      type: 'Child Health Review',
      mode: 'chat',
      status: 'scheduled',
      scheduledTime: new Date(2026, 0, 31, 9, 0),
    },
    {
      id: '5',
      date: '2026-01-29',
      time: '03:00 PM',
      patient: 'Mrs. Sarah Williams',
      patientId: 'patient5',
      type: 'General Consultation',
      mode: 'video',
      status: 'cancelled',
      scheduledTime: new Date(2026, 0, 29, 15, 0),
    },
  ];

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'video':
        return <Video className="w-5 h-5" />;
      case 'audio':
        return <Phone className="w-5 h-5" />;
      case 'chat':
        return <MessageSquare className="w-5 h-5" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: 'bg-green-100 text-green-700',
      confirmed: 'bg-blue-100 text-blue-700',
      upcoming: 'bg-blue-100 text-blue-700',
      scheduled: 'bg-purple-100 text-purple-700',
      cancelled: 'bg-red-100 text-red-700',
      pending_doctor_confirmation: 'bg-yellow-100 text-yellow-700',
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  const canJoinConsultation = (appointment) => {
    const now = new Date();
    const scheduledTime = new Date(appointment.scheduledTime);
    const fifteenMinutesBefore = new Date(scheduledTime.getTime() - 15 * 60 * 1000);
    const thirtyMinutesAfter = new Date(scheduledTime.getTime() + 30 * 60 * 1000);
    
    return now >= fifteenMinutesBefore && now <= thirtyMinutesAfter && appointment.status !== 'cancelled';
  };

  const handleJoinConsultation = (appointment, mode) => {
    navigate(`/doctor/consultation/${appointment.id}`, {
      state: {
        appointment: {
          ...appointment,
          mode: mode,
          doctor: 'You',
          specialty: 'OB/GYN Specialist',
        },
      },
    });
  };

  const handleConfirmAppointment = (appointmentId) => {
    doctorConfirmAppointment(appointmentId);
  };

  const handleStartConsulting = (appointment) => {
    setSelectedAppointment(appointment);
    setShowConsultingModal(true);
  };

  const handleSelectMode = (mode) => {
    handleJoinConsultation(selectedAppointment, mode);
    setShowConsultingModal(false);
  };

  const filteredAppointments = mockAppointments.filter(apt => {
    const matchesSearch = apt.patient.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         apt.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || apt.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const appointmentsByDate = filteredAppointments.reduce((acc, apt) => {
    if (!acc[apt.date]) {
      acc[apt.date] = [];
    }
    acc[apt.date].push(apt);
    return acc;
  }, {});

  return (
    <div className="p-6 md:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="p-3 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl shadow-lg shadow-emerald-500/30">
              <Calendar className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                Appointments
              </h1>
              <p className="text-gray-600 mt-1">Manage your consultation schedule</p>
            </div>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by patient name or appointment type..."
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="upcoming">Upcoming</option>
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Appointments List */}
        <div className="space-y-6">
          {Object.keys(appointmentsByDate).length > 0 ? (
            Object.keys(appointmentsByDate).sort().reverse().map(date => (
              <div key={date}>
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-emerald-600" />
                  {new Date(date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </h2>
                <div className="grid gap-4">
                  {appointmentsByDate[date].map(appointment => (
                    <div
                      key={appointment.id}
                      className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-start gap-4">
                            <div className="p-3 bg-emerald-50 rounded-xl">
                              {getModeIcon(appointment.mode)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-bold text-gray-900">
                                  {appointment.patient}
                                </h3>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(appointment.status)}`}>
                                  {appointment.status}
                                </span>
                              </div>
                              <p className="text-gray-600 mb-1">{appointment.type}</p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {appointment.time}
                                </span>
                                <span className="capitalize flex items-center gap-1">
                                  {getModeIcon(appointment.mode)}
                                  {appointment.mode} call
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {appointment.status === 'pending_doctor_confirmation' && (
                            <button
                              onClick={() => handleConfirmAppointment(appointment.id)}
                              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-600 transition-all shadow-lg shadow-blue-500/30 flex items-center gap-2"
                            >
                              <CheckCircle className="w-5 h-5" />
                              Confirm Appointment
                            </button>
                          )}
                          {canJoinConsultation(appointment) && appointment.status === 'confirmed' && (
                            <button
                              onClick={() => handleStartConsulting(appointment)}
                              className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-bold hover:from-emerald-700 hover:to-green-700 transition-all shadow-lg shadow-emerald-500/30 flex items-center gap-2 hover:scale-105"
                            >
                              <Stethoscope className="w-5 h-5" />
                              Start Consulting
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/doctor/patient-record/${appointment.patientId}`)}
                            className="px-6 py-3 border-2 border-emerald-600 text-emerald-600 rounded-xl font-semibold hover:bg-emerald-50 transition-all"
                          >
                            View Record
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-12 text-center">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">No appointments found</h3>
              <p className="text-gray-600">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </div>

      {/* Start Consulting Modal */}
      {showConsultingModal && selectedAppointment && (
        <StartConsultingModal
          appointment={selectedAppointment}
          onSelectMode={handleSelectMode}
          onClose={() => setShowConsultingModal(false)}
        />
      )}
    </div>
  );
};

export default DoctorAppointments;
